import * as _ from "lodash";
import {action, observable, toJS, flow, makeObservable} from "mobx";
import { appStore, reportStore } from "../";
import { EAIClient } from "Services/EAIClient";
import { Comment } from "./comments";
import { saveAs } from "file-saver";
import { Label } from "../labelStore";
import { Attachment } from "Stores/attachmentStore";
import { Student } from "Stores/commonStore";
import {StrategicPlanItem} from "Stores/strategicPlanStore";



export interface Permissions {
    edit_fields: string[]
    assignee: boolean
    can_add_comment: boolean
    can_add_milestones: boolean
    can_delete_milestones: boolean
    can_edit_name: boolean
    can_edit_assignee: boolean
    can_edit_collaborators: boolean
    can_edit_committees: boolean
    can_edit_course: boolean
    can_edit_ddd: boolean
    can_edit_evaluation_date: boolean
    can_edit_interpretation: boolean
    can_edit_labels: boolean
    can_edit_students: boolean
    can_edit_objective: boolean
    can_edit_status: boolean
    can_edit_target: boolean
    can_edit_milestones: boolean
    can_view_comment: boolean
    can_edit_strategic_plan: boolean
    can_edit_snapshots: boolean
    can_add_attachments: boolean
    owner: boolean
    collaborator: boolean
}

export interface Collaborator {
    [idx: string]: string

    faculty_ruuid: string
    email: string
    name: string
}

export interface Observation {
    [idx: string]: string | number | boolean | Collaborator[] | Permissions | object | undefined | null

    ruuid: string
    name: string
    school_id: string
    updated: string
    created: string
    comments: Comment[]
    committee_id: number
    committee_name: string
    committees: { id: number, name: string }[]
    dashboard: string
    dashboard_id: number | null
    display_id: number
    labels: { [idx: string]: Label[] }
    dashboard_interpretation: string
    dashboard_params: string
    created_by: string
    course_id: number
    course: string
    dashboard_thumb_url: string
    dashboard_image_url: string
    dashboard_snapshot_url: string
    initial_attachment_id: string
    assignee_ruuid: string
    assignee_name: string
    assignee_prefix?: string
    assignee_first_name: string
    assignee_last_name: string
    assignee_suffix?: string
    assignee_email: string
    collaborators: Collaborator[]
    strategic_plan_items: StrategicPlanItem[]
    students: Student[]
    created_by_id: string
    created_by_name: string
    created_by_prefix?: string
    created_by_first_name: string
    created_by_last_name: string
    created_by_suffix?: string
    created_by_email: string
    permissions: Permissions
    type: string
    isSelected?: boolean // only used in report selection
    attachments: Attachment[]
    strategic_plan_item_id: number
    ui_url: string
}

export interface Task extends Observation {
    status: string
    target: string
    objective: string
    ddd: string
    assignee_change_due_date: boolean
    evaluation_date?: string
    target_met?: boolean
    send_followup?: boolean
    closed_dashboard_image_url: string
    closed_dashboard_snapshot_url: string
    closed_attachment_id: string
}

export type Ticket = Observation | Task

export class TicketStore {

    @observable ticket: Ticket;
    @observable tickets?: Ticket[];
    @observable allTickets: Ticket[];
    @observable comments: Comment[];
    @observable comment?: Comment;
    @observable isProcessing: boolean;
    @observable filter?: Partial<Task>;
    @observable allSelected?: boolean;
    @observable isFetchingTickets?: boolean = false;
    @observable fetchingPDF: boolean | string = false;

    client = new EAIClient();

    constructor() {
        makeObservable(this);
    }

    @flow
    * fetchTickets(strategicPlanOnly: boolean, pageSize?: number, pageNumber?: number): any{
        this.isFetchingTickets = true;
        try {
            const resp = yield this.client.fetchTickets(strategicPlanOnly, pageSize, pageNumber);
            this.tickets = resp.data.data;
            this.allTickets = resp.data.data;
            if(!this.tickets || this.tickets.length === 0) return;
            this.tickets = this.tickets.map((t: Ticket) => {
                if(!t.assignee) t.assignee = "";
                if(!t.created_by) t.created_by = "";
                if(!t.ddd) t.ddd = "";
                if(!t.dashboard) t.dashboard = "";
                return t;
            });
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isFetchingTickets = false;
        }
    }

    @flow
    * exportAllActionPlans(){
        try {
            this.client.exportAllActionPlans();
        } catch(e) {
            appStore.addError(e);
        }
    }


    @flow
    * getTicketById(id: string): any{
        try {
            const resp = yield this.client.fetchTicketById(id);
            this.ticket = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        }
    }

    @flow
    * getTicketsForDashboard(id: number, pageSize?: number, pageNumber?: number, isFirstLoadOnDashboard?: boolean): any{
        this.isFetchingTickets = true;
        try {
            const resp = yield this.client.fetchTicketsForDashboard(id, pageSize, pageNumber, isFirstLoadOnDashboard);
            this.allTickets = resp.data.data;
            this.tickets = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isFetchingTickets = false;
        }
    }

    @flow
    * createOrUpdateTicket(data: any, userChosenSchoolInfo?: any): any{
        this.isProcessing = true;
        if(data.type === "observation" && !userChosenSchoolInfo.is_admin){
            delete data.evaluation_date;
            delete data.assignee_change_due_date;
        }

        try {
            let resp;
            if(data.ruuid){
                resp = yield this.client.updateTicket(data);
            } else {
                resp = yield this.client.createTicket(data);
            }
            this.ticket = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * deleteTicket(ticketId: string): any{
        this.isProcessing = true;
        try {
            yield this.client.deleteTicket(ticketId);
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * updateCollaborators(ticketId: string, adds: Collaborator[], removes: Collaborator[]): any{
        this.isProcessing = true;
        try {
            const data = {
                adds: adds.map((collaborator: Collaborator) => collaborator.faculty_ruuid),
                removes: removes.map((collaborator: Collaborator) => collaborator.faculty_ruuid),
            };
            return this.client.bulkChangeCollaboratorsForTicket(ticketId, data);
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * addCommentToTicket(ticketId: string, comment: Partial<Comment>, skipSnapshotParam = false): any{
        this.isProcessing = true;
        this.comment = undefined;
        try {
            const resp = yield this.client.addCommentToTicket(ticketId, comment, skipSnapshotParam);
            this.comment = resp.data.data;
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.isProcessing = false;
        }
    }

    @flow
    * getTicketPDF(ticketId: string): any{
        this.fetchingPDF = ticketId;
        try {
            const resp = yield this.client.downloadTicketPDF(ticketId);
            saveAs(resp.data, "ActionPlan.pdf");
        } catch(e) {
            appStore.addError(e);
        } finally {
            this.fetchingPDF = false;
        }

    }

    @action
    filterList(predicate?: Partial<Ticket>){
        if(!this.allTickets || this.allTickets.length === 0) return;
        if(!predicate){
            this.tickets = this.allTickets;
            return;
        }

        const data: (Task | Observation)[] = this.allTickets;
        const keys = Object.keys(predicate);

        this.tickets = _.filter(data, (d: Task | Observation) => {
            let isIncluded = true;
            keys.forEach((k: string) => {
                if(predicate && predicate.hasOwnProperty(k)){
                    isIncluded = isIncluded && d[k] === predicate[k];
                }
            });
            return isIncluded;
        });
        this._updateSelectionStatus();
    }


    @action
    /*
     * This must be the first filter to perform
     */
    filterListByDate(createdFrom?: Date, createdTo?: Date, dueFrom?: Date, dueTo?: Date){
        let data = this.allTickets;
        this.tickets = data;

        if(!createdFrom && !createdTo && !dueFrom && !dueTo) return;

        data = this._filterListByFromTo("created", data, createdFrom, createdTo);
        data = this._filterListByFromTo("evaluation_date", data, dueFrom, dueTo);

        this.tickets = _.sortBy(_.uniqBy(data, "display_id"), "display_id");
        this._updateSelectionStatus();
    }

    private _filterListByFromTo(type: string, tickets: Ticket[], from?: Date, to?: Date){
        if(from && to){
            const _from: Date = new Date(from);
            const _to: Date = new Date(to);
            const offSetFrom = _from.getTimezoneOffset();
            const offSetTo = _to.getTimezoneOffset();
            _from.setMinutes(_from.getMinutes() + offSetFrom);
            _to.setMinutes(_to.getMinutes() + offSetTo);
            _to.setDate(_to.getDate() + 1);

            tickets = _.filter<Ticket>(tickets, (d: Ticket) => {
                const x = new Date(d[type] as string);
                const offSet = x.getTimezoneOffset();
                x.setMinutes(x.getMinutes() + offSet);
                return x >= _from && x <= _to;
            });
        } else if(from){
            const _from = new Date(from);
            const offSet = _from.getTimezoneOffset();
            _from.setMinutes(_from.getMinutes() + offSet);

            tickets = _.filter<Ticket>(tickets, (d: Ticket) => {
                const x = new Date(d[type] as string);
                const offSet = x.getTimezoneOffset();
                x.setMinutes(x.getMinutes() + offSet);
                return _from <= x;
            });
        }
        if(to){
            const _to = new Date(to);
            const offSet = _to.getTimezoneOffset();
            _to.setMinutes(_to.getMinutes() + offSet);
            _to.setDate(_to.getDate()+1);

            tickets = _.filter<Ticket>(tickets, (d: Ticket) => {
                const x = new Date(d[type] as string);
                const offSet = x.getTimezoneOffset();
                x.setMinutes(x.getMinutes() + offSet);
                return _to >= x;
            });
        }
        return tickets;
    }

    @action
    filterListByTypeStatus(open?: boolean, closedMet?: boolean, closedNotMet?: boolean, observations?: boolean){
        const data = this.tickets;
        let tickets: Ticket[] = [];

        if(open){
            tickets = [...tickets, ..._.filter(data, {type: "task", status: "open"})];
        }
        if(closedMet){
            tickets = [...tickets, ..._.filter(data, {type: "task", status: "closed", target_met: true})];
        }
        if(closedNotMet){
            tickets = [...tickets, ..._.filter(data, {type: "task", status: "closed", target_met: false})];
        }
        if(observations){
            tickets = [...tickets, ..._.filter(data, {type: "observation"})];
        }

        this.tickets = _.sortBy(_.uniqBy(tickets, "display_id"), "display_id");
        this._updateSelectionStatus();
    }

    @action
    filterListByFilters(strategicPlanItems: number[], assignees: string[], committees: number[], students: string[], courses: number[], creators: string[], dashboards: number[], labels: number[]){
        const predicateArr: ((t: Ticket) => boolean)[] = [];

        if(strategicPlanItems.length > 0){
            predicateArr.push((t: Ticket) => {
                if (strategicPlanItems.indexOf(t.strategic_plan_item_id) > -1) {
                    return true;
                }
                if (t.strategic_plan_items) {
                    return t.strategic_plan_items.some(x => strategicPlanItems.includes(Number(x.id)));
                }
                return false;
            });
        }

        if(assignees.length > 0){
            predicateArr.push((t: Ticket) => assignees.indexOf(t.assignee_ruuid) > -1);
        }

        if(creators.length > 0){
            predicateArr.push((t: Ticket) => creators.indexOf(t.created_by_id) > -1);
        }
        if(committees.length > 0){
            predicateArr.push((t: Ticket) => {
                if(t.committees && t.committees.length > 0){
                    return t.committees.some(c => committees.indexOf(c.id) > -1);
                }
                return committees.indexOf(t.committee_id) > -1;
            });
        }
        if(students.length > 0){
            predicateArr.push((t: Ticket) => _.intersection(students, t.students.map(x => x.ruuid)).length > 0);
        }
        if(courses.length > 0){
            predicateArr.push((t: Ticket) => courses.indexOf(t.course_id) > -1);
        }
        if(dashboards.length > 0){
            predicateArr.push((t: Ticket) => t.dashboard_id !== null && dashboards.indexOf(t.dashboard_id) > -1);
        }
        if(labels.length > 0){
            predicateArr.push((t: Ticket) => {
                const groupedLabels = toJS(t).labels;
                const groups = Object.keys(groupedLabels);
                if(groups.length){
                    const ungrouped = groups.reduce((prev, curr) => {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        return prev.concat(groupedLabels[curr].map(l => l.id));
                    }, []);
                    return !!_.intersection(labels, ungrouped).length;
                } else {
                    return false;
                }
            });
        }

        // AND the predicates together
        this.tickets = _.filter(toJS(this.tickets), (t: Ticket) => {
            // For each predicate, return false if any are false
            // otherwise return true
            for(let i = 0, length = predicateArr.length; i < length; i++){
                if(!predicateArr[i](t)){
                    return false;
                }
            }
            return true;
        });
        this.tickets = _.sortBy(this.tickets, "display_id");
        this._updateSelectionStatus();
    }

    @action
    filterListByKeywords(summary?: (string | number | boolean | undefined)[]){
        if(!summary || (summary && summary.length === 0)) return;

        if(summary && summary.length > 0){
            let tickets: Ticket[] = [];
            for(const keyword of summary){
                if(keyword){
                    const keywordString = keyword.toString();
                    tickets = _.concat(tickets,
                        _.filter(this.tickets, (t: Ticket) => t.dashboard_interpretation.toLowerCase().indexOf(keywordString) > -1),
                        _.filter(this.tickets, (t: Task) => t.ddd.toLowerCase().indexOf(keywordString) > -1),
                        _.filter(this.tickets, (t: Task) => t.objective.toLowerCase().indexOf(keywordString) > -1));
                }
            }
            this.tickets = tickets;
        }

        this.tickets = _.sortBy(_.uniqBy(this.tickets, "display_id"), "display_id");
        this._updateSelectionStatus();
    }

    @action
    filterListByCustom(filteredList: Ticket[]) {
        this.tickets = filteredList;
        this._updateSelectionStatus();
    }

    @action
    toggleSelectionById(ruuid: string){
        if(!this.tickets) return;
        let isAllSelected = true;
        // seems inefficient, but needed to force re-draw of list
        this.tickets = this.tickets.map((t: Ticket) => {
            if(t.ruuid === ruuid) t.isSelected = !t.isSelected;
            isAllSelected = isAllSelected && (t.isSelected || false);
            return t;
        });
        this.allSelected = isAllSelected;
    }

    @action
    setSelectionAll(isSelected: boolean){
        if(!this.tickets) return;
        this.allSelected = isSelected;
        this.tickets = this.tickets.map((t: Ticket) => {
            t.isSelected = isSelected;
            return t;
        });
    }

    // Sorts tickets according to urgency. Open tasks are on the top, closed
    // tasks whose target was not met are next, followed by closed tasks that
    // were met, and observations in last place. Or to put it in
    // semi-mathematical notation:
    //      open > closed and not met > closed and met > observation
    statusSort = (t1: Ticket, t2: Ticket) => {
        if(t1.type === "observation" && t2.type === "observation"){
            return 0;
        }

        if(t1.type !== "observation" && t2.type === "observation"){
            return -1;
        }

        if(t1.type === "observation" && t2.type !== "observation"){
            return 1;
        }

        const taskOne = t1 as Task;
        const taskTwo = t2 as Task;

        if(!taskOne.target_met && !taskTwo.target_met){
            if(taskOne.status === taskTwo.status){
                return 0;
            } else if(taskOne.status! < taskTwo.status!){
                return 1;
            } else {
                return -1;
            }
        }

        if(taskOne.target_met === taskTwo.target_met){
            return 0;
        } else if(taskOne.target_met && !taskTwo.target_met){
            return 1;
        } else if(!taskOne.target_met && taskTwo.target_met){
            return -1;
        } else {
            return 1;
        }
    };

    creatorSort = (t1: Ticket, t2: Ticket) => {
        if(t1.created_by_last_name === t2.created_by_last_name) return 0;
        return t1.created_by_last_name > t2.created_by_last_name ? 1 : -1;
    };

    assigneeSort = (t1: Ticket, t2: Ticket) => {
        if(t1.assignee_last_name === t2.assignee_last_name) return 0;
        return t1.assignee_last_name > t2.assignee_last_name ? 1 : -1;
    };

    private _updateSelectionStatus(){
        if(!this.tickets) return;
        this.tickets = this.tickets.map((t) => {
            const isSelected = _.findIndex(reportStore.selectedTickets, {ruuid: t.ruuid});
            if(isSelected > -1) t.isSelected = true;
            return t;
        });
    }
}
